import produce from 'immer';
import { Box, Card, Skeleton, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import {
  dateUtils,
  IAuditEvent,
  IAuditFieldUpdate,
  useStateStyles,
  processAuditValue,
} from '@diana/core';
import { Fragment, useMemo } from 'react';
import { DateTime } from 'luxon';

interface IDataChangeDetails {
  keyword: string | null;
  data: IAuditEvent | undefined;
  isLoading: boolean;
}

export const DataChangeDetails = (props: IDataChangeDetails) => {
  const { data, isLoading } = props;
  const items = useMemo(() => {
    const changeMap = new Map<string, IAuditFieldUpdate>();
    for (const fieldUpdate of data?.activity?.fieldUpdates ?? []) {
      const key = `${fieldUpdate.path}/${fieldUpdate.fieldName}`;
      const existing = changeMap.get(key);
      if (existing) {
        changeMap.set(
          key,
          produce(existing, (value) => {
            value.fromValue = fieldUpdate.fromValue || existing.toValue || existing.fromValue;
            value.toValue = fieldUpdate.toValue;
            value.timeStamp = fieldUpdate.timeStamp;
          }),
        );
        continue;
      }
      changeMap.set(key, fieldUpdate);
    }

    return Array.from(changeMap.values()).sort(
      (a, b) => DateTime.fromISO(a.timeStamp).toMillis() - DateTime.fromISO(b.timeStamp).toMillis(),
    );
  }, [data?.activity?.fieldUpdates]);

  return (
    <Box>
      {isLoading && [...Array(4)].map((n, index) => <Skeleton key={index} width="60%" />)}

      {items.map((item, index) => (
        <DataChangeDetailsItem key={index} item={item} index={index} />
      ))}
    </Box>
  );
};

function DataChangeDetailsItem({ item, index }: { item: IAuditFieldUpdate; index: number }) {
  const { palette } = useTheme();
  const fieldName = String(item.fieldName);
  const timeStamp = String(item.timeStamp);
  const fromValue = processAuditValue(item.fromValue);
  const toValue = processAuditValue(item.toValue);
  const fromValueStateStyles = useStateStyles(item.fromValueColor);
  const toValueStateStyles = useStateStyles(item.toValueColor);

  const loc = useMemo(() => {
    try {
      return dateUtils.renderLocalDateTimeZone(timeStamp);
    } catch (error) {
      console.error(error);
      return '';
    }
  }, [timeStamp]);

  const utc = useMemo(() => {
    try {
      return dateUtils.renderUTCDateTimeZone(timeStamp);
    } catch (error) {
      console.error(error);
      return '';
    }
  }, [timeStamp]);

  return (
    <Box key={index} pt={0.5}>
      <Stack gap={0.3}>
        <Typography color="text.secondary">{fieldName}:</Typography>
        <Tooltip
          title={
            <Fragment>
              {loc ? <Typography fontSize="0.7rem">LOC: {loc}</Typography> : null}
              {utc ? <Typography fontSize="0.7rem">UTC: {utc}</Typography> : null}
            </Fragment>
          }
          placement="top-start"
          followCursor={true}
        >
          <Stack direction="row" alignItems="center">
            {fromValue != null && fromValue.length > 0 && (
              <>
                <Card
                  className="contained"
                  sx={{
                    px: 1,
                    py: 0.2,
                    border: `1px solid ${palette.border.main}`,
                    borderRadius: '4px',
                    ...fromValueStateStyles,
                  }}
                >
                  <Typography fontSize="0.85rem">{fromValue}</Typography>
                </Card>
              </>
            )}
            <ArrowForward sx={{ fontSize: '14px' }} />
            <Card
              className="contained"
              sx={{
                px: 1,
                py: 0.2,
                border: `1px solid ${palette.border.main}`,
                borderRadius: '4px',
                ...toValueStateStyles,
              }}
            >
              <Typography fontSize="0.85rem">{toValue ? toValue : <span>&nbsp;</span>}</Typography>
            </Card>
          </Stack>
        </Tooltip>
      </Stack>
    </Box>
  );
}
